import { useState, useEffect } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { t } from "@/translations";

export default function Navbar() {
  const { lang, setLang } = useLang();
  const tr = t[lang].nav;

  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: tr.home, href: "#", dropdown: tr.homeDropdown },
    { label: tr.online, href: "#", dropdown: tr.onlineDropdown },
    { label: tr.consult, href: "#", dropdown: [] as { label: string; href: string }[] },
    { label: tr.resources, href: "#", dropdown: tr.resourcesDropdown },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="https://aba-school.ru/startschool" className="flex-shrink-0">
          <img
            src="https://fs-thb01.getcourse.ru/fileservice/file/thumbnail/h/1c4480280b79b4e0c8d920f5078036ed.png/s/f1200x/a/47075/sc/49"
            alt="ABASCHOOL"
            className="h-10 object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item, idx) => (
            <div
              key={idx}
              className="relative"
              onMouseEnter={() => item.dropdown.length > 0 && setOpenDropdown(idx)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <a
                href={item.href}
                className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-md ${
                  scrolled ? "text-gray-700 hover:text-[#4caf50]" : "text-white hover:text-[#a5d6a7]"
                }`}
              >
                {item.label}
                {item.dropdown.length > 0 && (
                  <i className="ri-arrow-down-s-line ml-1 text-xs" />
                )}
              </a>
              {item.dropdown.length > 0 && openDropdown === idx && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg py-2 min-w-[260px] border border-gray-100">
                  {item.dropdown.map((sub, si) => (
                    <a
                      key={si}
                      href={sub.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#4caf50] transition-colors cursor-pointer"
                    >
                      {sub.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right buttons */}
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => setLang("ru")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              lang === "ru"
                ? "bg-[#4caf50] text-white hover:bg-[#43a047]"
                : "border border-[#4caf50] text-[#4caf50] hover:bg-[#f1f8e9]"
            }`}
          >
            RUS
          </button>
          <button
            onClick={() => setLang("en")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              lang === "en"
                ? "bg-[#4caf50] text-white hover:bg-[#43a047]"
                : "border border-[#4caf50] text-[#4caf50] hover:bg-[#f1f8e9]"
            }`}
          >
            ENG
          </button>
          <button className="px-4 py-1.5 text-sm font-semibold rounded-md bg-[#4caf50] text-white hover:bg-[#43a047] transition-colors cursor-pointer whitespace-nowrap">
            {tr.login}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-9 h-9 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${scrolled ? "text-gray-800" : "text-white"} ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          {navItems.map((item, idx) => (
            <div key={idx}>
              <a href={item.href} className="block py-2 text-sm font-medium text-gray-700 hover:text-[#4caf50] cursor-pointer">
                {item.label}
              </a>
              {item.dropdown.map((sub, si) => (
                <a key={si} href={sub.href} className="block pl-4 py-1.5 text-sm text-gray-500 hover:text-[#4caf50] cursor-pointer">
                  {sub.label}
                </a>
              ))}
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setLang("ru")}
              className={`px-4 py-1.5 text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                lang === "ru"
                  ? "bg-[#4caf50] text-white"
                  : "border border-[#4caf50] text-[#4caf50]"
              }`}
            >
              RUS
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1.5 text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                lang === "en"
                  ? "bg-[#4caf50] text-white"
                  : "border border-[#4caf50] text-[#4caf50]"
              }`}
            >
              ENG
            </button>
            <button className="px-4 py-1.5 text-sm font-semibold rounded-md bg-[#4caf50] text-white cursor-pointer whitespace-nowrap">
              {tr.login}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
